import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getRequestOffers, deleteRequestOffer } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { cn } from "../../lib/utils";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Grid3X3,
  List,
  Table,
  HandHeart,
  Calendar,
} from "lucide-react";

export default function RequestOfferPage() {
  useRequireSectionVisible("showRequestOffer");
  const { data: user } = useAuth();
  const { getLocationIdsForQuery, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteRequestOfferFn = useAction(deleteRequestOffer);

  // State for filters and view mode
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || "all",
  );
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "price">(
    "newest",
  );

  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch items with hub aggregation support
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery(getRequestOffers, {
    locationIds: getLocationIdsForQuery(),
    filters: {
      type: "REQUEST_OFFER",
    },
  });

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let filtered = items as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item: any) =>
          item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (item: any) => item.metadata?.category === selectedCategory,
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "price":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.price || 0) - (a.metadata?.price || 0),
        );
        break;
    }

    return filtered;
  }, [items, searchTerm, selectedCategory, sortBy]);

  const handleDelete = async (itemId: string) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteRequestOfferFn({ itemId });
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  const getCategoryColor = (category: string) => {
    return category === "REQUEST"
      ? "bg-success/10 text-success border border-success/20"
      : "bg-primary/10 text-primary border border-primary/20";
  };

  const getConditionColor = (condition: string) => {
    const colors = {
      NEW: "bg-success/10 text-success border border-success/20",
      LIKE_NEW: "bg-primary/10 text-primary border border-primary/20",
      GOOD: "bg-warning/10 text-warning border border-warning/20",
      FAIR: "bg-secondary/10 text-secondary border border-secondary/20",
      POOR: "bg-destructive/10 text-destructive border border-destructive/20",
    };
    return (
      colors[condition as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Request & Offer</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Request and offer items within {selectedLocation?.name || "Location"} (
            {filteredItems.length} items)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  >
                    <option value="all">All Categories</option>
                    <option value="REQUEST">Request</option>
                    <option value="OFFER">Offer</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">Title A-Z</option>
                    <option value="price">Price (High-Low)</option>
                  </select>
                </div>

                <Button className="w-full" onClick={() => setIsFilterExpanded(false)}>Apply</Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(`/locations/${selectedLocation.locationCode}/request-offer/add`);
                } else {
                  navigate("/request-offer/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline">Post Item</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-3 min-w-0 truncate">Item</div>
            <div className="col-span-1 min-w-0 truncate">Category</div>
            <div className="col-span-2 min-w-0 truncate">Price</div>
            <div className="col-span-2 min-w-0 truncate">Condition</div>
            <div className={user?.isAdmin ? "col-span-2 min-w-0 truncate" : "col-span-3 min-w-0 truncate"}>Author</div>
            <div className="col-span-1 min-w-0 truncate">Date</div>
            {user?.isAdmin && <div className="col-span-1 text-right min-w-0 truncate">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-2">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Category</option>
              <option value="REQUEST">Request</option>
              <option value="OFFER">Offer</option>
            </select>
          </div>
          <div className="col-span-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">A-Z</option>
              <option value="price">Price (High-Low)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  if (!user || !selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {!user ? "Authentication Required" : "No Location Selected"}
            </h2>
            <p className="text-gray-600">
              {!user 
                ? "You must be logged in to view items." 
                : "Please select a location to view items."}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          )}

          {!isLoading && !error && filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">No items found.</div>
          )}

          {!isLoading && !error && filteredItems.length > 0 && (
            <div className={cn(
              viewMode === "table" ? "w-full" : "p-6",
              viewMode === "cards" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"
            )}>
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    viewMode === "cards" 
                      ? enhancedPageLayoutStyles.card.container 
                      : viewMode === "list" 
                        ? "bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                        : "px-6 py-4 hover:bg-sidebar-hover transition-colors border-b border-gray-100 cursor-pointer"
                  )}
                  onClick={() => navigate(`/request-offer/${item.id}`)}
                >
                  {viewMode === "table" ? (
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-3">
                        <div className="font-medium text-gray-900 truncate">{item.title}</div>
                        <div className="text-xs text-gray-500 truncate">{item.description}</div>
                      </div>
                      <div className="col-span-1">
                        <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getCategoryColor(item.metadata?.category))}>
                          {item.metadata?.category}
                        </span>
                      </div>
                      <div className="col-span-2 font-bold text-success text-sm">
                        {item.metadata?.price ? `$${item.metadata.price}` : "-"}
                      </div>
                      <div className="col-span-2">
                        {item.metadata?.condition && (
                          <span className={cn("px-2 py-1 rounded-full text-[10px] font-medium", getConditionColor(item.metadata.condition))}>
                            {item.metadata.condition?.replace("_", " ")}
                          </span>
                        )}
                      </div>
                      <div className={user?.isAdmin ? "col-span-2" : "col-span-3"}>
                        <span className="text-sm text-gray-500 truncate block">
                          {item.author.name || item.author.email}
                        </span>
                      </div>
                      <div className="col-span-1 text-sm text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                      {user?.isAdmin && (
                        <div className="col-span-1 flex justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                          <EnhancedIconButton onClick={(e) => { e?.stopPropagation(); navigate(`/request-offer/${item.id}/edit`); }} icon={Edit} title="Edit" />
                          <EnhancedIconButton onClick={(e) => { e?.stopPropagation(); handleDelete(item.id); }} icon={Trash2} title="Delete" variant="danger" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={enhancedPageLayoutStyles.card.content}>
                      <div className={enhancedPageLayoutStyles.card.header}>
                        <span className={cn("px-2 py-1 text-xs font-medium rounded", getCategoryColor(item.metadata?.category))}>
                          {item.metadata?.category}
                        </span>
                        {item.metadata?.price && (
                          <div className="text-lg font-bold text-success">${item.metadata.price}</div>
                        )}
                      </div>
                      <h3 className={enhancedPageLayoutStyles.card.title}>{item.title}</h3>
                      <p className={enhancedPageLayoutStyles.card.description}>{item.description}</p>
                      <div className={enhancedPageLayoutStyles.card.footer}>
                        <div className={enhancedPageLayoutStyles.card.meta}>
                          by {item.author.name || item.author.email} • {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                        {user?.isAdmin && (
                          <div className="flex gap-2" onClick={e => e?.stopPropagation()}>
                            <EnhancedIconButton onClick={(e) => { e?.stopPropagation(); navigate(`/request-offer/${item.id}/edit`); }} icon={Edit} title="Edit" />
                            <EnhancedIconButton onClick={(e) => { e?.stopPropagation(); handleDelete(item.id); }} icon={Trash2} title="Delete" variant="danger" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
